<template>
  <div class="dashboard-wrapper">
    <div class="hero">
      <div class="hero-text">
        <div class="pill">请求态势</div>
        <h2>请求日志仪表盘</h2>
        <p class="sub">快速洞察请求来源、峰值时段与成功率，及时守护你的题库适配器。</p>
        <div class="chips">
          <span class="chip">累计请求 {{ stats.totalRequests }}</span>
          <span class="chip">今日 {{ stats.todayRequests }}</span>
          <span class="chip">运行 {{ uptimeText }}</span>
        </div>
      </div>
      <div class="hero-right">
        <a-segmented v-model:value="range" :options="rangeOptions" size="large" @change="fetchStats" />
        <div class="circle">
          <div class="circle-value">{{ successPercent }}%</div>
          <div class="circle-label">命中率</div>
        </div>
      </div>
    </div>

    <a-row :gutter="16" class="metric-row">
      <a-col :span="8">
        <a-card class="glass-card" :loading="loading" bordered>
          <div class="card-title">总览</div>
          <div class="stat-block">
            <div>
              <div class="stat-label">总请求</div>
              <div class="stat-value">{{ stats.totalRequests }}</div>
            </div>
            <div>
              <div class="stat-label">今日</div>
              <div class="stat-value accent">{{ stats.todayRequests }}</div>
            </div>
            <div>
              <div class="stat-label">平均耗时</div>
              <div class="stat-value">{{ avgDurationText }}</div>
            </div>
          </div>
          <div class="stat-foot">
            <span>运行时长</span>
            <span class="accent">{{ uptimeText }}</span>
          </div>
        </a-card>
      </a-col>
      <a-col :span="8">
        <a-card class="glass-card" :loading="loading" bordered>
          <div class="card-title">来源分布</div>
          <div v-if="sourceBars.length" class="bar-list">
            <div v-for="item in sourceBars" :key="item.name" class="bar-item">
              <div class="bar-label">
                <span>{{ item.name }}</span>
                <span class="light">{{ item.value }}</span>
              </div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: item.width + '%' }"></div>
              </div>
            </div>
          </div>
          <div v-else class="empty">暂无来源数据</div>
        </a-card>
      </a-col>
      <a-col :span="8">
        <a-card class="glass-card" :loading="loading" bordered>
          <div class="card-title">高频时段</div>
          <div class="peak">
            <div class="peak-value">{{ peakHourText }}</div>
            <div class="peak-desc">使用最频繁的时间段</div>
          </div>
          <div class="mini-chart">
            <div
              v-for="item in hourBars"
              :key="item.hour"
              class="mini-bar"
              :style="{ height: item.height + '%', background: item.isPeak ? 'linear-gradient(180deg, #69c0ff, #1890ff)' : '#e6f7ff' }"
              :title="`${item.hour}:00 - ${item.count}次`"
            ></div>
          </div>
        </a-card>
      </a-col>
    </a-row>

    <a-row :gutter="16" class="metric-row">
      <a-col :span="12">
        <a-card class="glass-card" :loading="loading" bordered>
          <div class="card-title">请求热力（近两周）</div>
          <div class="heatmap">
            <div
              v-for="item in hourBars"
              :key="`heat-${item.hour}`"
              class="heat-cell"
              :style="{ background: `rgba(24, 144, 255, ${Math.max(item.opacity, 0.15)})` }"
            >
              <div class="heat-hour">{{ item.hour }}:00</div>
              <div class="heat-count">{{ item.count }}</div>
            </div>
          </div>
        </a-card>
      </a-col>

      <a-col :span="12">
        <a-card class="glass-card" :loading="loading" bordered>
          <div class="card-title">活跃用户</div>
          <div v-if="canViewStats && stats.topUsers?.length" class="user-list">
            <div v-for="(user, idx) in stats.topUsers" :key="user.userId" class="user-item">
              <div class="badge">{{ idx + 1 }}</div>
              <div class="user-meta">
                <div class="name">{{ user.authUsername || user.authUserId || '未知用户' }}</div>
                <div class="sub">设置 ID: {{ user.userId }}</div>
              </div>
              <div class="count">{{ user.count }} 次</div>
            </div>
          </div>
          <div v-else class="empty">暂无用户统计</div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getRequestStats } from '@/api/TikuComponentApi'
import { message } from 'ant-design-vue'
import { hasPermission, PERMISSIONS } from '@/utils/permissions'

export default defineComponent({
  name: 'RequestDashboard',
  setup() {
    const stats = ref({
      totalRequests: 0,
      todayRequests: 0,
      uniqueUsers: 0,
      uptimeSeconds: 0,
      sources: [],
      hours: [],
      topUsers: [],
      avgDuration: 0,
      successRate: 0
    })
    const loading = ref(false)
    const range = ref('7d')
    const rangeOptions = [
      { label: '24H', value: '24h' },
      { label: '7天', value: '7d' },
      { label: '14天', value: '14d' },
      { label: '全部', value: 'all' },
    ]
    const canViewStats = hasPermission(PERMISSIONS.REQUEST_LOG_STATS_READ)
    const router = useRouter()

    const buildStart = (val) => {
      const now = Date.now()
      switch (val) {
        case '24h':
          return new Date(now - 24 * 60 * 60 * 1000)
        case '7d':
          return new Date(now - 7 * 24 * 60 * 60 * 1000)
        case '14d':
          return new Date(now - 14 * 24 * 60 * 60 * 1000)
        default:
          return null
      }
    }

    const fetchStats = () => {
      loading.value = true
      const params = {}
      const start = buildStart(range.value)
      if (start) {
        params.start = start.toISOString()
      }
      getRequestStats(params).then(res => {
        if (res.status === 200) {
          stats.value = { ...stats.value, ...(res.data || {}) }
        }
      }).finally(() => {
        loading.value = false
      })
    }

    const formatDuration = (ms) => {
      if (!ms) return '0 ms'
      if (ms < 1000) return `${ms} ms`
      return `${(ms / 1000).toFixed(2)} s`
    }

    const formatUptime = (seconds) => {
      if (!seconds) return '0s'
      const d = Math.floor(seconds / 86400)
      const h = Math.floor((seconds % 86400) / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const parts = []
      if (d) parts.push(`${d}天`)
      if (h) parts.push(`${h}小时`)
      if (m || (!d && !h)) parts.push(`${m}分钟`)
      return parts.join(' ')
    }

    const uptimeText = computed(() => formatUptime(stats.value.uptimeSeconds))
    const successPercent = computed(() => Math.round((stats.value.successRate || 0) * 1000) / 10)
    const avgDurationText = computed(() => formatDuration(stats.value.avgDuration))

    const sourceBars = computed(() => {
      const list = stats.value.sources || []
      const max = Math.max(...list.map(i => Number(i.value) || 0), 1)
      return list.map(item => ({
        ...item,
        width: Math.max((Number(item.value) || 0) / max * 100, 8),
      }))
    })

    const hourBars = computed(() => {
      const hours = stats.value.hours && stats.value.hours.length ? stats.value.hours : Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }))
      const max = Math.max(...hours.map(h => Number(h.count) || 0), 1)
      const peak = hours.reduce((p, c) => (Number(c.count) > Number(p.count) ? c : p), { hour: 0, count: 0 })
      return hours.map(h => {
        const count = Number(h.count) || 0
        return {
          hour: h.hour,
          count,
          height: Math.max(count / max * 100, 5),
          opacity: Math.max(count / max, 0.1),
          isPeak: h.hour === peak.hour,
        }
      })
    })

    const peakHourText = computed(() => {
      const peak = hourBars.value.reduce((p, c) => (c.count > p.count ? c : p), { hour: 0, count: 0 })
      if (!peak.count) return '暂无数据'
      return `${peak.hour}:00 - ${peak.count} 次`
    })

    onMounted(() => {
      if (!canViewStats) {
        message.warning('缺少请求统计查看权限')
        router.push('/adapter/user-dashboard')
        return
      }
      fetchStats()
    })

    return {
      stats,
      loading,
      range,
      rangeOptions,
      uptimeText,
      successPercent,
      sourceBars,
      hourBars,
      peakHourText,
      avgDurationText,
      fetchStats,
      canViewStats
    }
  }
})
</script>

<style scoped>
.dashboard-wrapper {
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: #f0f2f5;
}

.hero {
  background: #fff;
  color: #333;
  padding: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
}

.hero-text h2 {
  margin: 12px 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #1f1f1f;
}

.sub {
  color: #666;
  max-width: 520px;
  margin-bottom: 16px;
  line-height: 1.6;
}

.pill {
  display: inline-flex;
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.chips {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.chip {
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  color: #555;
  padding: 6px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
}

.hero-right {
  display: flex;
  align-items: center;
  gap: 32px;
}

.circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #fff;
  border: 4px solid #e6f7ff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.1);
  position: relative;
}

.circle::after {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 4px solid #1890ff;
  border-right-color: transparent;
  border-bottom-color: transparent;
  transform: rotate(-45deg);
}

.circle-value {
  font-size: 28px;
  font-weight: 700;
  color: #1890ff;
}

.circle-label {
  color: #888;
  font-size: 12px;
  margin-top: 4px;
}

.metric-row {
  width: 100%;
}

.glass-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  color: #333;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
  height: 100%;
  transition: all 0.3s;
}

.glass-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.card-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
  color: #1f1f1f;
  display: flex;
  align-items: center;
}

.card-title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 16px;
  background: #1890ff;
  margin-right: 8px;
  border-radius: 2px;
}

.stat-block {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
}

.stat-label {
  color: #8c8c8c;
  font-size: 13px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f1f1f;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.accent {
  color: #1890ff;
}

.stat-foot {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  color: #8c8c8c;
  font-size: 13px;
}

.bar-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bar-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-label {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #555;
}

.light {
  color: #999;
}

.bar-track {
  height: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #69c0ff, #1890ff);
  border-radius: 4px;
}

.empty {
  text-align: center;
  color: #ccc;
  padding: 32px 0;
}

.peak {
  margin-top: 8px;
  margin-bottom: 24px;
}

.peak-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f1f1f;
}

.peak-desc {
  color: #8c8c8c;
  margin-top: 4px;
}

.mini-chart {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 4px;
  align-items: flex-end;
  height: 100px;
  margin-top: 16px;
}

.mini-bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  transition: all 0.2s;
}

.mini-bar:hover {
  transform: scaleY(1.1);
  transform-origin: bottom;
}

.heatmap {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.heat-cell {
  padding: 16px;
  border-radius: 8px;
  color: #fff;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s;
}

.heat-cell:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.heat-hour {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 4px;
}

.heat-count {
  font-size: 20px;
  font-weight: 700;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f9f9f9;
  transition: background 0.2s;
}

.user-item:hover {
  background: #f0f7ff;
}

.badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e6f7ff;
  color: #1890ff;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
}

.user-meta .name {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.user-meta .sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.count {
  margin-left: auto;
  font-weight: 600;
  color: #1890ff;
  background: #fff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
}

@media (max-width: 992px) {
  .hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
  .metric-row {
    flex-direction: column;
  }
  .heatmap {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
